import React, { useState, useEffect, useMemo, useId } from "react";
import type { AdminChatThread } from "../types";
import { Button } from "@/components/ui/button";
import type { RouterOutputs } from "@/trpc";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useRouter, useSearch } from "@tanstack/react-router";
import { AdminChatThreadList } from "./AdminChatThreadList";
import { AdminChatThreadView } from "./AdminChatThreadView";
import { useTRPC, useTRPCClient } from "@/trpc";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";

const PAGE_SIZE = 50;

type AdminChatProps = {
  onCreateChat: () => void;
};

const AdminChat: React.FC<AdminChatProps> = ({ onCreateChat }) => {
  const filterId = useId();
  const router = useRouter();
  const search = useSearch({ strict: false });
  const [selectedThread, setSelectedThread] = useState<AdminChatThread | null>(
    null,
  );
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [organizationId, setOrganizationId] = useState<string | undefined>(
    undefined,
  );
  const [organizationBusinessUnitId, setOrganizationBusinessUnitId] = useState<
    string | undefined
  >(undefined);
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const { data: businessUnitsData } = useQuery(
    trpc.organization.getAllBusinessUnits.queryOptions(),
  );
  type BusinessUnit =
    RouterOutputs["organization"]["getAllBusinessUnits"][number] & {
      organization: { id: string; name: string };
    };
  const allBusinessUnits = useMemo(
    () => (businessUnitsData ?? []) as BusinessUnit[],
    [businessUnitsData],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const organizationOptions = useMemo(() => {
    if (allBusinessUnits.length === 0) {
      return [];
    }
    const map = new Map<string, string>();
    allBusinessUnits.forEach((unit) => {
      if (!map.has(unit.organization.id)) {
        map.set(unit.organization.id, unit.organization.name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({
      value: id,
      label: name,
    }));
  }, [allBusinessUnits]);

  const businessUnitOptions = useMemo(() => {
    if (allBusinessUnits.length === 0) {
      return [];
    }
    return allBusinessUnits
      .filter((unit) =>
        organizationId ? unit.organization.id === organizationId : true,
      )
      .map((unit) => ({
        value: unit.id,
        label: unit.name,
        organizationId: unit.organization.id,
      }));
  }, [allBusinessUnits, organizationId]);

  const queryParams = useMemo(
    () => ({
      search: searchQuery || undefined,
      organizationId,
      organizationBusinessUnitId,
    }),
    [searchQuery, organizationId, organizationBusinessUnitId],
  );

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["adminChatThreads", queryParams],
      queryFn: ({ pageParam = 0 }) =>
        trpcClient.chat.adminListChatThreads.query({
          ...queryParams,
          offset: pageParam * PAGE_SIZE,
          limit: PAGE_SIZE,
        }),
      getNextPageParam: (lastPage, allPages) =>
        lastPage.hasMore ? allPages.length : undefined,
      initialPageParam: 0,
      refetchInterval: searchQuery ? undefined : 30_000,
    });

  const allThreads = useMemo(
    () =>
      data?.pages.flatMap((page) => page.threads) ?? ([] as AdminChatThread[]),
    [data],
  );

  const filteredThreads = useMemo(
    () =>
      allThreads.filter((thread) =>
        search.archived
          ? thread.archived_at !== null
          : thread.archived_at === null,
      ),
    [allThreads, search.archived],
  );

  useEffect(() => {
    const threadId = search.thread;
    if (!threadId) {
      setSelectedThread(null);
      return;
    }

    const thread = filteredThreads.find((t) => t.id === threadId);
    if (thread) {
      setSelectedThread(thread);
      return;
    }

    const fallbackThread = allThreads.find((t) => t.id === threadId);
    if (fallbackThread) {
      setSelectedThread(fallbackThread);
    } else {
      setSelectedThread(null);
      router.navigate({
        // @ts-expect-error route search is intentionally merged
        search: (prev) => {
          const { thread: _thread, ...rest } = prev;
          return rest;
        },
      });
    }
  }, [search.thread, filteredThreads, allThreads, router]);

  const handleSelectThread = (thread: AdminChatThread) => {
    router.navigate({
      // @ts-expect-error route search is intentionally merged
      search: (prev) => ({
        ...prev,
        thread: thread.id,
      }),
    });
  };

  const handleBack = () => {
    router.navigate({
      // @ts-expect-error route search is intentionally merged
      search: (prev) => {
        const { thread: _thread, ...rest } = prev;
        return rest;
      },
    });
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col overflow-hidden bg-slate-50">
      <div
        className={`shrink-0 border-b border-slate-200 bg-white ${selectedThread ? "hidden md:block" : ""}`}
      >
        <div className="mx-auto flex w-full min-w-0 max-w-7xl flex-col gap-3 p-3 sm:p-4 md:flex-row md:items-end md:justify-between md:gap-4">
          <Button onClick={onCreateChat} className="w-full md:w-auto">
            Create Chat
          </Button>
          <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2 md:ml-auto md:max-w-2xl md:gap-4">
            <div className="flex min-w-0 flex-col gap-2">
              <Label htmlFor={`${filterId}-organization`}>Organization</Label>
              <Select
                value={organizationId ?? "all"}
                onValueChange={(value) => {
                  const next = value === "all" ? undefined : value;
                  setOrganizationId(next);
                  setOrganizationBusinessUnitId(undefined);
                }}
              >
                <SelectTrigger
                  id={`${filterId}-organization`}
                  className="w-full min-w-0 [&>span]:truncate"
                >
                  <SelectValue placeholder="All organizations" />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  <SelectItem value="all">All organizations</SelectItem>
                  {organizationOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className="[overflow-wrap:anywhere]"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <Label htmlFor={`${filterId}-business-unit`}>Business Unit</Label>
              <Select
                value={organizationBusinessUnitId ?? "all"}
                onValueChange={(value) => {
                  setOrganizationBusinessUnitId(
                    value === "all" ? undefined : value,
                  );
                }}
                disabled={businessUnitOptions.length === 0}
              >
                <SelectTrigger
                  id={`${filterId}-business-unit`}
                  className="w-full min-w-0 [&>span]:truncate"
                >
                  <SelectValue placeholder="All business units" />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  <SelectItem value="all">All business units</SelectItem>
                  {businessUnitOptions.map((unit) => (
                    <SelectItem
                      key={unit.value}
                      value={unit.value}
                      className="[overflow-wrap:anywhere]"
                    >
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col md:flex-row">
        <div
          className={`flex h-full min-h-0 min-w-0 shrink-0 flex-col overflow-hidden border-slate-200 bg-white md:border-r ${selectedThread ? "hidden md:flex" : ""} w-full md:w-80 xl:w-96`}
        >
          <AdminChatThreadList
            threads={filteredThreads}
            onSelectThread={handleSelectThread}
            selectedThread={selectedThread}
            searchQuery={searchInput}
            onSearchChange={setSearchInput}
            onLoadMore={handleLoadMore}
            hasMore={hasNextPage}
            isLoadingMore={isFetchingNextPage}
          />
        </div>
        <div
          className={`min-h-0 min-w-0 flex-1 flex-col ${selectedThread ? "flex" : "hidden md:flex"}`}
        >
          {selectedThread ? (
            <AdminChatThreadView thread={selectedThread} onBack={handleBack} />
          ) : isLoading && filteredThreads.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p>Loading conversations…</p>
            </div>
          ) : (
            <div className="hidden h-full min-w-0 flex-1 flex-col items-center justify-center p-6 text-center text-slate-500 md:flex">
              <p className="text-lg font-medium text-slate-700">
                Select a conversation to inspect details
              </p>
              <p className="mt-2 max-w-sm text-sm text-slate-500">
                Use the filters and search to drill down into organization
                activity. Messages load instantly once a thread is selected.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminChat;
